import { UIConfigurationListDto } from '@lmt/shared-types';
import { Button } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styles from './ConfigurationList.module.scss';

interface Props {
  currentConfigurationId?: number;
  onConfigurationChange: (newConfigurationId: number) => void;
  configurations?: UIConfigurationListDto[];
  editConfig: (id: number) => void;
  createConfig: () => void;
  isPersonalEditAllowed: boolean;
  isSharedEditAllowed: boolean;
  testId?: string;
  configName: string;
}

export const ConfigurationList: React.FC<Props> = (props) => {
  const i18n = useScopedTranslation('ui_configurator');
  const {
    onConfigurationChange,
    configurations,
    currentConfigurationId,
    editConfig,
    createConfig,
    isPersonalEditAllowed,
    isSharedEditAllowed,
    configName,
  } = props;
  // this is used to close the popup and reset
  const [key, setKey] = useState(1);
  const closeDropdown = useCallback(() => {
    setKey((key) => ++key);
  }, []);

  useEffect(
    function setDefaultConfiguration() {
      if (!configurations?.length) {
        return;
      }
      // current configuration is set and was not deleted
      if (
        currentConfigurationId &&
        configurations.some((x) => x.id === currentConfigurationId)
      ) {
        return;
      }
      // otherwise set default configuration
      const defaultConfiguration = configurations.find(
        (config) => config.isDefault,
      );
      if (defaultConfiguration) {
        onConfigurationChange(defaultConfiguration.id);
        return;
      }
      onConfigurationChange(configurations[0].id);
    },
    [configurations, configurations?.length],
  );

  const renderConfigurationOption = useCallback(
    (config: UIConfigurationListDto) => {
      const canEdit = config.isShared
        ? isSharedEditAllowed && isPersonalEditAllowed
        : isPersonalEditAllowed;
      return (
        <div className={styles.controls}>
          {canEdit && (
            <a
              className={styles.editLink}
              onClick={(event) => {
                event.stopPropagation(); // to prevent onOptionClick
                editConfig(config.id);
              }}
            >
              {i18n.t(configName + '.edit_button', {
                defaultValue: configName,
              })}
            </a>
          )}
        </div>
      );
    },
    [configName],
  );

  const renderDropdownBottom = useMemo(() => {
    if (!isPersonalEditAllowed) {
      return null;
    }
    return (
      <div
        className={styles.bottomContainer}
        onMouseDown={(e) => e.preventDefault()}
        data-not-empty={!!configurations?.length}
      >
        <Button
          className={styles.addButton}
          title={i18n.t(configName + '.add_button', {
            defaultValue: configName,
          })}
          onClick={() => {
            closeDropdown();
            createConfig();
          }}
        />
      </div>
    );
  }, [configName]);

  const options = useMemo(() => {
    if (!configurations) {
      return [];
    }
    return configurations
      .map((configuration) => {
        const displayValue: string = i18n.t('names.' + configuration.name, {
          defaultValue: configuration.name,
        });
        return {
          ...configuration,
          displayValue: displayValue.toLowerCase(),
        };
      })
      .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
  }, [configurations]);

  return (
    <DropDownInput
      key={key}
      options={options}
      classes={{
        option: styles.option,
      }}
      rootClassName={styles.container}
      onValueChanged={(option) => onConfigurationChange(option!.id)}
      idFunction={'id'}
      getOptionLabel={'name'}
      required={true}
      postfixRenderer={renderConfigurationOption}
      useIdFunctionAsValue={true}
      value={currentConfigurationId}
      popupFooter={renderDropdownBottom}
      testId={props.testId}
    />
  );
};
